import { Link } from "react-router-dom";
import { ArrowUpRight, Hammer, Building2, Wrench, Globe, Sparkles, Award } from "lucide-react";
import { thumb } from "@/lib/img";
import SeoCopyBlock from "@/components/SeoCopyBlock";
import HeroSlides from "@/components/HeroSlides";
import { SEO_COPY } from "@/data/seoCopy";
import useSEO from "@/hooks/useSEO";
import QuickQuote from "@/components/QuickQuote";
import ReviewsBlock from "@/components/ReviewsBlock";
import { useBlogPosts } from "@/hooks/useBlog";
import { regions, expos, COMPANY, getExposByRegion, getCitiesByRegion } from "@/data/clientsData";
import { ExpoCard } from "@/components/ClientGrid";
import {
  organizationSchema,
  websiteSchema,
  localBusinessSchema,
  itemListSchema,
  faqSchema,
} from "@/lib/seo-schemas";

const services = [
  { icon: Sparkles, title: "Custom Stall Design", desc: "Hero concepts, 3D renders and walk-throughs by senior in-house designers." },
  { icon: Hammer, title: "Turnkey Fabrication", desc: "End-to-end fabrication, transport, on-site installation and dismantling." },
  { icon: Building2, title: "Country Pavilions", desc: "National pavilions with shared branding and exhibitor pods, as built for India at VivaTech Paris and Iraq at ITB Berlin." },
  { icon: Wrench, title: "Modular Systems", desc: "Reusable modular kits — perfect for road-shows and repeat exhibitors." },
  { icon: Globe, title: "Global Project Mgmt", desc: "Local crew, local permits, single point of contact across 11 countries." },
  { icon: Award, title: "Fixed Price, On-Time Handover", desc: "Stands handed over at Salone del Mobile, Interzoo, MWC, AAPEX, SMM Hamburg and Prodexpo." },
];

const HomePage = () => {
  useSEO({
    title: SEO_COPY.home.metaTitle,
    description:
      SEO_COPY.home.metaDescription,
    keywords:
      "exhibition stall designers, expo booth builders, exhibition stand contractor UK, expo booth builder USA, exhibition stall Europe, expo booth builder Asia, exhibition stall Delhi, AAPEX booth builder, Interzoo stand",
    image: "https://stagehookglobal.com/projects/smm-hamburg/smm-hamburg-1.webp",
    path: "/",
    jsonLd: [
      organizationSchema(),
      websiteSchema(),
      localBusinessSchema(),
      itemListSchema(
        "Regions we build exhibition stalls in",
        regions.map((r) => ({ name: `${r.name} — Expo Booth Builder`, path: `/region/${r.slug}` }))
      ),
      faqSchema([
        {
          question: "Where does Stagehook Global build exhibition stalls?",
          answer:
            "Stagehook Global is a turnkey exhibition stall designer & fabricator with delivery across the UK, USA, Europe (Italy, Germany, France, Spain, Netherlands, Switzerland, Austria) and Asia (India, Singapore).",
        },
        {
          question: "Which trade fairs and expos have you built stands at?",
          answer:
            "We have built stands at 60+ expos including Salone del Mobile Milano, Cosmoprof Bologna, EICMA, Interzoo Nürnberg, Intersolar Munich, ANUGA Cologne, ITB Berlin, Vivatech Paris, MWC Barcelona, ISE Amsterdam, AAPEX Las Vegas, SupplySide West, SMM Hamburg, Prodexpo Moscow, IDEM Singapore and Indus Food Greater Noida.",
        },
        {
          question: "Do you offer turnkey stand design and fabrication?",
          answer:
            "Yes — we handle everything end-to-end: 3D design concept, engineering, fabrication, transport, on-site installation, event support and dismantling. One point of contact worldwide.",
        },
        {
          question: "How fast can I get a quote?",
          answer:
            "Send us your expo, dates and stand size — we deliver concept renders and a budget estimate within 72 hours.",
        },
      ]),
    ],
  });

  const blogPosts = useBlogPosts();
  const regionHeroes = new Set(regions.map((r) => r.heroImage));
  const featuredExpos = expos.filter((e) => !regionHeroes.has(e.heroImage)).slice(0, 6);

  return (
    <main data-testid="home-page" className="pt-[72px]">
      {/* HERO */}
      <section className="relative overflow-hidden min-h-[92vh] flex items-end grain">
        <HeroSlides />
        <div className="hero-blob" style={{ bottom: "-300px", left: "-260px", opacity: 0.18 }} />

        <div className="shg-container w-full relative z-10 pb-24 pt-32">
          <div className="max-w-3xl">
            <div className="shg-eyebrow fade-up">Worldwide · Since 2015</div>
            <h1 className="font-display text-[13vw] md:text-[7vw] lg:text-[96px] leading-[0.92] mt-6 fade-up delay-1">
              We build the <span style={{ color: "var(--red)" }}>booths</span><br />
              the world remembers.
            </h1>
            <p className="text-lg md:text-xl text-[var(--text-soft)] max-w-2xl mt-8 leading-relaxed fade-up delay-2">
              Stagehook Global is a turnkey exhibition stall design &amp; fabrication studio.
              From Milano to Las Vegas — we engineer hero stands across <span className="text-white font-semibold">{COMPANY.countriesServed} countries</span>.
            </p>
            <div className="flex flex-wrap gap-3 mt-10 fade-up delay-3">
              <Link to="/contact" data-testid="hero-cta" className="btn-primary">
                Start your project <ArrowUpRight size={16} />
              </Link>
              <Link to="/projects" data-testid="hero-projects" className="btn-ghost">
                Explore Projects
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-y" style={{ background: "var(--bg-soft)", borderColor: "var(--line)" }}>
        <div className="shg-container py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { num: COMPANY.projectsBuilt, label: "Stalls delivered" },
            { num: COMPANY.countriesServed, label: "Countries served" },
            { num: COMPANY.exposCovered, label: "Expos covered" },
            { num: COMPANY.yearsExperience, label: "Years experience" },
          ].map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="font-display text-5xl lg:text-6xl">
                <span style={{ color: "var(--red)" }}>{s.num}</span>
              </div>
              <div className="font-mono text-[10px] tracking-[0.25em] text-[var(--text-muted)] mt-2 uppercase">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* REGIONS */}
      <section data-testid="regions-section" className="py-28">
        <div className="shg-container">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
            <div className="max-w-2xl">
              <div className="section-tag mb-4"><span className="dot" />Where we build</div>
              <h2 className="font-display text-5xl md:text-6xl leading-[0.95]">
                Expo booth builders<br />
                across <span style={{ color: "var(--red)" }}>4 regions</span>.
              </h2>
            </div>
            <p className="text-[var(--text-soft)] max-w-md text-[15px]">
              USA · Europe · Russia · Asia — click any region to see the cities we build in and the expos we&apos;ve delivered stands at.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {regions.map((r, i) => {
              const rExpos = getExposByRegion(r.slug);
              const rCities = getCitiesByRegion(r.slug);
              return (
                <Link
                  key={r.slug}
                  to={`/region/${r.slug}`}
                  data-testid={`region-card-${r.slug}`}
                  className="shg-card group block fade-up"
                  style={{ animationDelay: `${i * 0.08}s` }}
                >
                  <div className="aspect-[4/5] overflow-hidden relative" style={{ background: "var(--bg-soft)" }}>
                    {rExpos.length > 0 && (
                      <img
                        src={thumb(rExpos[rExpos.length - 1].heroImage)}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        data-testid={`region-card-bg-${r.slug}`}
                        className="absolute inset-0 w-full h-full object-cover scale-105 blur-[2px] opacity-70 transition-opacity duration-700 group-hover:opacity-90"
                      />
                    )}
                    <div
                      className="absolute inset-0"
                      style={{ background: "linear-gradient(180deg, rgba(7,7,10,0.2) 0%, rgba(7,7,10,0.95) 100%)" }}
                    />
                    <div className="absolute top-5 left-5 text-4xl">{r.flag}</div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="font-display text-4xl md:text-5xl text-white group-hover:text-[var(--red)] transition-colors mb-2">
                        {r.shortName}
                      </div>
                      <div className="text-sm text-[var(--text-soft)] mb-4 line-clamp-2">
                        {r.tagline}
                      </div>
                      <div className="flex gap-4 font-mono text-[10px] tracking-[0.15em] text-[var(--text-soft)]">
                        <span>{rExpos.length} {rExpos.length === 1 ? "EXPO" : "EXPOS"}</span>
                        <span>·</span>
                        <span>{rCities.length} {rCities.length === 1 ? "CITY" : "CITIES"}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="services-section" className="py-24" style={{ background: "var(--bg-soft)" }}>
        <div className="shg-container">
          <div className="mb-14 max-w-3xl">
            <div className="section-tag mb-4"><span className="dot" />What we do</div>
            <h2 className="font-display text-5xl md:text-6xl leading-[0.95]">
              A full-stack exhibition partner —<br />
              <span style={{ color: "var(--red)" }}>design</span> to dismantle.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: "var(--line)" }}>
            {services.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  data-testid={`service-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="p-9 group transition-colors duration-300"
                  style={{ background: "var(--bg-soft)" }}
                >
                  <Icon className="text-[var(--red)] mb-6" size={28} strokeWidth={1.5} />
                  <h3 className="font-display text-2xl mb-3">{s.title}</h3>
                  <p className="text-[var(--text-soft)] text-sm leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <SeoCopyBlock id="home" />

      {/* FEATURED EXPOS */}
      <section data-testid="featured-expos" className="py-28">
        <div className="shg-container">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="section-tag mb-4"><span className="dot" />Featured Expos</div>
              <h2 className="font-display text-5xl leading-[0.95]">
                Stands at the world&apos;s<br />
                <span style={{ color: "var(--red)" }}>biggest stages</span>.
              </h2>
            </div>
            <Link to="/projects" className="shg-link text-sm">View all expos →</Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredExpos.map((e) => (
              <ExpoCard key={e.slug} expo={e} />
            ))}
          </div>
        </div>
      </section>

      <ReviewsBlock />

      {/* BLOG */}
      <section data-testid="home-blog" className="py-24" style={{ background: "var(--bg-soft)" }}>
        <div className="shg-container">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <div className="section-tag mb-4"><span className="dot" />Pricing & guides</div>
              <h2 className="font-display text-4xl md:text-5xl leading-[0.95]">What a stand really costs.</h2>
            </div>
            <Link to="/blog" data-testid="home-blog-all" className="btn-ghost self-start">All guides <ArrowUpRight size={16} /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {blogPosts.slice(0, 3).map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`home-blog-${p.slug}`} className="shg-card p-6 group block">
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase mb-3" style={{ color: "var(--red)" }}>{p.category}</div>
                <h3 className="font-display text-xl leading-tight group-hover:text-[var(--red)] transition-colors mb-3">{p.title}</h3>
                <p className="text-sm text-[var(--text-soft)] line-clamp-3">{p.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28">
        <div className="shg-container">
          <div
            className="relative overflow-hidden rounded-3xl p-12 md:p-20 grain"
            style={{ background: "linear-gradient(135deg, #16161e 0%, #0c0c12 100%)", border: "1px solid var(--line-strong)" }}
          >
            <div
              className="absolute -right-32 -top-32 w-[500px] h-[500px] rounded-full"
              style={{ background: "radial-gradient(circle, var(--red) 0%, transparent 70%)", filter: "blur(80px)", opacity: 0.35 }}
            />
            <div className="relative z-10 grid lg:grid-cols-2 gap-10 items-center">
            <div className="max-w-3xl">
              <div className="shg-eyebrow mb-6">Ready to exhibit?</div>
              <h2 className="font-display text-5xl md:text-7xl leading-[0.95] mb-8">
                Let&apos;s build your next<br />
                <span style={{ color: "var(--red)" }}>showstopper.</span>
              </h2>
              <p className="text-[var(--text-soft)] text-lg max-w-xl mb-10">
                Tell us your expo, dates and stand size — we&apos;ll send concept renders within 72 hours.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact" data-testid="cta-contact" className="btn-primary">
                  Request a Quote <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
            <QuickQuote source="home" />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
